import React from "react";
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Collapse,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    Add,
    DeleteOutline,
    DragIndicator,
    ExpandLess,
    ExpandMore,
} from "@mui/icons-material";
import { DragDropContext, Draggable, type DropResult } from "react-beautiful-dnd";
import { StrictModeDroppable } from "../../../molecules/StrictModeDroppable";
import {
    type Question,
    type WorkflowQuestion,
    type WorkflowTriggerAction,
    InclusionMode,
} from "../../../../types/rubric";
import type { AnswerValue } from "../../../../types/answerSet";

type WorkflowQuestionWithClientId = WorkflowQuestion & {
    clientId: string;
}

const operatorOptions = [{ value: "IS", label: "is" }];
const actionOptions = [
    { value: "GO_TO", label: "Go to question" },
    { value: "STOP", label: "Stop workflow" },
    { value: "SET_ANSWERS", label: "Set answers" },
    { value: "AUTOFILL", label: "Autofill answer" },
];

// Action configuration for dynamic input rendering
const actionConfig: Record<string, {
    hasInput: boolean;
    inputType?: string;
    inputLabel?: string;
    placeholder?: string;
    needsToAnswerValueIds?: boolean;
}> = {
    GO_TO: {
        hasInput: true,
        inputType: 'question-select',
        inputLabel: 'Target Question',
        placeholder: 'Select target question'
    },
    STOP: {
        hasInput: false
    },
    SET_ANSWERS: {
        hasInput: true,
        inputType: 'question-select',
        inputLabel: 'Target Question',
        placeholder: 'Select target question',
        needsToAnswerValueIds: true
    },
    AUTOFILL: {
        hasInput: true,
        inputType: 'question-select',
        inputLabel: 'Target Question',
        placeholder: 'Select target question',
        needsToAnswerValueIds: true
    }
};

const questionCardPalette = {
    background: "#F7FDFF",
    header: "#E9F3FF",
    border: "#BBDCFB",
    triggerBackground: "#F7FDFF",
};

export interface QuestionsSectionProps {
    questions: WorkflowQuestionWithClientId[];
    validTargetQuestions?: WorkflowQuestionWithClientId[];
    onDragEnd: (result: DropResult) => void;
    collapsedSections: Record<string, boolean>;
    onToggleSection: (questionKey: string) => void;
    onRemoveQuestion: (clientId: string) => void;
    onAddTrigger: (clientId: string) => void;
    onRemoveTrigger: (clientId: string, triggerIndex: number) => void;
    onTriggerAnswersChange: (clientId: string, triggerIndex: number, values: AnswerValue[]) => void;
    onTriggerTargetChange: (clientId: string, triggerIndex: number, targetId: number | undefined) => void;
    onTriggerActionChange: (clientId: string, triggerIndex: number, action: WorkflowTriggerAction) => void;
    onTriggerToAnswersChange?: (clientId: string, triggerIndex: number, answerIds: number[]) => void;
    questionsAvailableToAdd?: Question[];
    onAddQuestion: (questionId: number) => void;
    showAddQuestion?: boolean;
    droppableId: string;
}

const getActiveAnswers = (question: Question) => {
    if (!question || !question.answers) {
        return [];
    }
    return question.answers?.filter((answer) => answer.active).filter((answer) => {
        if (question.inclusionMode === InclusionMode.INCLUDE) {
            return question.selectedAnswerIds?.includes(answer.id);
        } else {
            return !question.selectedAnswerIds?.includes(answer.id);
        }
    });
}

const QuestionsSection: React.FC<QuestionsSectionProps> = ({
    questions,
    validTargetQuestions,
    onDragEnd,
    collapsedSections,
    onToggleSection,
    onRemoveQuestion,
    onAddTrigger,
    onRemoveTrigger,
    onTriggerAnswersChange,
    onTriggerTargetChange,
    onTriggerActionChange,
    onTriggerToAnswersChange,
    questionsAvailableToAdd = [],
    onAddQuestion,
    showAddQuestion = true,
    droppableId,
}) => {

    const renderTriggerSection = (workflowQuestion: WorkflowQuestionWithClientId) => {
        const question = workflowQuestion.question;
        const clientId = workflowQuestion.clientId;

        const activeAnswers = getActiveAnswers(question);
        // Build target options based on question type
        // SystemQuestions can target both SystemQuestions and WorkflowQuestions
        // WorkflowQuestions can only target other WorkflowQuestions
        let targetOptions: { id: number; name: string }[] = [];
        targetOptions = validTargetQuestions
            .filter((q) => q.question.id !== question.id)
            .map((q) => ({
                id: q.question.id,
                name: q.question.name,
            }));            

        

        return (
            <Stack spacing={1.5} sx={{ width: "100%", flexDirection: "column", backgroundColor: questionCardPalette.background }}>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ md: "center" }}
                >
                    <Typography variant="subtitle2">Triggers</Typography>
                    <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => onAddTrigger(clientId)}
                        disabled={activeAnswers.length === 0}
                    >
                        Add Trigger
                    </Button>
                </Stack>

                {(workflowQuestion.triggers ?? []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No triggers yet. Add one to define the workflow logic.
                    </Typography>
                ) : (
                    (workflowQuestion.triggers ?? []).map((trigger, triggerIndex) => (
                        <Box
                            key={`${clientId}-${triggerIndex}`}
                            sx={{
                                border: `1px solid ${questionCardPalette.border}`,
                                backgroundColor: questionCardPalette.triggerBackground,
                                borderRadius: "3px",
                                p: 2,
                            }}
                        >
                            <Stack spacing={2}>
                                <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={1}
                                    alignItems={{ md: "center" }}
                                >
                                    <Typography variant="subtitle2">If</Typography>
                                    <TextField
                                        value={workflowQuestion.question.name}
                                        disabled
                                        size="small"
                                        fullWidth
                                        sx={{ flex: 1, minWidth: 0 }}
                                    />

                                    <FormControl sx={{ flex: 1, minWidth: 0 }} size="small" fullWidth>
                                        <InputLabel id={`operator-${clientId}-${triggerIndex}`}>Operator</InputLabel>
                                        <Select
                                            labelId={`operator-${clientId}-${triggerIndex}`}
                                            label="Operator"
                                            value={operatorOptions[0].value}
                                            disabled
                                        >
                                            {operatorOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Autocomplete<AnswerValue, true, false, false>
                                        multiple
                                        disableCloseOnSelect
                                        options={activeAnswers}
                                        getOptionLabel={(option) => option.name ?? option.displayText ?? ""}
                                        value={activeAnswers.filter((answer) => trigger.answerValueIds?.includes(answer.id))}
                                        onChange={(_, values: AnswerValue[]) =>
                                            onTriggerAnswersChange(clientId, triggerIndex, values)
                                        }
                                        renderOption={(props, option, { selected }) => {
                                            const { key, ...restProps } = props;
                                            const selectedAnswers = activeAnswers.filter((answer) => trigger.answerValueIds?.includes(answer.id));
                                            const allSelected = selectedAnswers.length === activeAnswers.length;
                                            const isFirstOption = activeAnswers.indexOf(option) === 0;
                                            
                                            return (
                                                <React.Fragment key={key}>
                                                    {isFirstOption && activeAnswers.length > 1 && (
                                                        <Box
                                                            component="li"
                                                            sx={{
                                                                fontWeight: 600,
                                                                borderBottom: "1px solid #e0e0e0",
                                                                py: 1,
                                                                px: 2,
                                                                cursor: "pointer",
                                                                "&:hover": { backgroundColor: "action.hover" },
                                                            }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (allSelected) {
                                                                    onTriggerAnswersChange(clientId, triggerIndex, []);
                                                                } else {
                                                                    onTriggerAnswersChange(clientId, triggerIndex, activeAnswers);
                                                                }
                                                            }}
                                                        >
                                                            {allSelected ? "Deselect All" : "Select All"}
                                                        </Box>
                                                    )}
                                                    <Box component="li" {...restProps}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selected}
                                                            readOnly
                                                            style={{ marginRight: 8 }}
                                                        />
                                                        {option.name ?? option.displayText ?? ""}
                                                    </Box>
                                                </React.Fragment>
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Answers" placeholder="Select answers" size="small" />
                                        )}
                                        fullWidth
                                        sx={{ flex: 1, minWidth: 0 }}
                                    />
                                </Stack>
                                <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={1}
                                    alignItems={{ md: "center" }}
                                >
                                    <Typography variant="subtitle2">Then</Typography>
                                    <FormControl sx={{ minWidth: 160 }} size="small">
                                        <InputLabel id={`action-${clientId}-${triggerIndex}`}>Action</InputLabel>
                                        <Select
                                            labelId={`action-${clientId}-${triggerIndex}`}
                                            label="Action"
                                            value={trigger.action ?? "GO_TO"}
                                            onChange={(event) =>
                                                onTriggerActionChange(
                                                    clientId,
                                                    triggerIndex,
                                                    event.target.value as WorkflowTriggerAction,
                                                )
                                            }
                                        >
                                            {actionOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {actionConfig[trigger.action || 'GO_TO']?.hasInput && (
                                        <FormControl fullWidth size="small">
                                            <InputLabel id={`trigger-target-${clientId}-${triggerIndex}`}>
                                                {actionConfig[trigger.action || 'GO_TO']?.inputLabel || 'Action Parameter'}
                                            </InputLabel>
                                            <Select
                                                labelId={`trigger-target-${clientId}-${triggerIndex}`}
                                                label={actionConfig[trigger.action || 'GO_TO']?.inputLabel || 'Action Parameter'}
                                                value={trigger.toQuestionId}
                                                onChange={(event) => onTriggerTargetChange(clientId, triggerIndex, Number(event.target.value))}
                                            >
                                                {targetOptions.length === 0 ? (
                                                    <MenuItem disabled value="">
                                                        {actionConfig[trigger.action || 'GO_TO']?.placeholder || 'Add another question to target'}
                                                    </MenuItem>
                                                ) : (
                                                    targetOptions.map((option) => (
                                                        <MenuItem key={option.id} value={option.id}>
                                                            {/* deepcode ignore DomXss: False positive - option.name is internal state */}
                                                            {option.name}
                                                        </MenuItem>
                                                    ))
                                                )}
                                            </Select>
                                        </FormControl>
                                    )}
                                    {actionConfig[trigger.action || 'GO_TO']?.needsToAnswerValueIds && (trigger.toQuestionId) && (() => {
                                        // Find the target question to get its answers
                                        const targetQuestion = (validTargetQuestions || questions).find(
                                            (q) => q.question.id === trigger.toQuestionId
                                        );
                                        const targetAnswers = getActiveAnswers(targetQuestion?.question);

                                        if (targetAnswers.length === 0) {
                                            return null;
                                        }
                                        
                                        const isAutofill = trigger.action === "AUTOFILL";
                                        
                                        return (
                                            <FormControl fullWidth size="small">
                                                <InputLabel id={`trigger-to-answers-${clientId}-${triggerIndex}`}>
                                                    {isAutofill ? "Answer to Autofill" : "Answers to Set"}
                                                </InputLabel>
                                                {isAutofill ? (
                                                    <Select
                                                        labelId={`trigger-to-answers-${clientId}-${triggerIndex}`}
                                                        placeholder={isAutofill ? "Answer to Autofill" : "Answers to Set"}
                                                        value={trigger.toAnswerValueIds?.[0] ?? ""}
                                                        onChange={(event) => {
                                                            if (onTriggerToAnswersChange) {
                                                                const answerId = event.target.value ? Number(event.target.value) : undefined;
                                                                onTriggerToAnswersChange(
                                                                    clientId,
                                                                    triggerIndex,
                                                                    answerId !== undefined ? [answerId] : [],
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        {targetAnswers.map((answer) => (
                                                            <MenuItem key={answer.id} value={answer.id}>
                                                                {answer.name ?? answer.displayText ?? ""}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                ) : (
                                                    <Autocomplete<AnswerValue, true, false, false>
                                                        multiple
                                                        options={targetAnswers}
                                                        getOptionLabel={(option) => option.name ?? option.displayText ?? ""}
                                                        value={targetAnswers.filter((answer) => trigger.toAnswerValueIds?.includes(answer.id))}
                                                        onChange={(_, values: AnswerValue[]) => {
                                                            if (onTriggerToAnswersChange) {
                                                                onTriggerToAnswersChange(
                                                                    clientId,
                                                                    triggerIndex,
                                                                    values.map((v) => v.id as number),
                                                                );
                                                            }
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField {...params} label="Answers to Set" placeholder="Select answers" size="small" />
                                                        )}
                                                    />
                                                )}
                                            </FormControl>
                                        );
                                    })()}
                                    <Tooltip title="Remove trigger">
                                        <IconButton
                                            color="secondary"
                                            onClick={() => onRemoveTrigger(clientId, triggerIndex)}
                                            aria-label="Remove trigger"
                                        >
                                            <DeleteOutline />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        </Box>
                    ))
                )}
            </Stack>
        );
    };

    return (
        <Stack spacing={2} sx={{ width: "100%" }}>
            <DragDropContext onDragEnd={onDragEnd}>
                <StrictModeDroppable droppableId={droppableId}>
                    {(droppableProvided) => (
                        // deepcode ignore DomXss: False positive - react-beautiful-dnd droppableProps are safe library props
                        <Stack spacing={2} ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                            {questions.map((workflowQuestion, index) => {
                                
                                
                                const question = workflowQuestion?.question;
                                if (!question) {
                                    return null;
                                }
                                const questionKey =
                                    workflowQuestion.clientId ?? String(question.id ?? index);
                                const isCollapsed = collapsedSections[questionKey] ?? false;
                                const draggableId = workflowQuestion.clientId ?? `workflow-question-${questionKey}`;

                                return (
                                    <Draggable key={draggableId} draggableId={draggableId} index={index}>
                                        {(draggableProvided, snapshot) => (
                                            <Paper
                                                ref={draggableProvided.innerRef}
                                                {...draggableProvided.draggableProps}
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: questionCardPalette.background,
                                                    borderColor: questionCardPalette.border,
                                                    borderWidth: 1,
                                                    borderStyle: "solid",
                                                    flexDirection: "column",
                                                    opacity: snapshot.isDragging ? 0.98 : 1,
                                                    transition: "opacity 0.2s ease",
                                                    boxShadow: "none",
                                                }}
                                            >
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    sx={{ py: 1.5, px: 2 }}
                                                >
                                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                        <Box
                                                            component="span"
                                                            {...draggableProvided.dragHandleProps}
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                cursor: "grab",
                                                                color: "text.secondary",
                                                            }}
                                                        >
                                                            <DragIndicator fontSize="small" />
                                                        </Box>
                                                        <Typography variant="subtitle1">
                                                            {question.name}
                                                        </Typography>
                                                        <Chip
                                                            label={question.questionType}
                                                            size="small"
                                                            color="primary"
                                                        />
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Tooltip title={isCollapsed ? "Expand section" : "Collapse section"}>
                                                            <IconButton onClick={() => onToggleSection(questionKey)}>
                                                                {isCollapsed ? <ExpandMore /> : <ExpandLess />}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Remove question">
                                                            <IconButton onClick={() => onRemoveQuestion(workflowQuestion.clientId)}>
                                                                <DeleteOutline />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </Stack>
                                                <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                                                    <Divider sx={{ mx: 2 }} />
                                                    <Box sx={{ backgroundColor: questionCardPalette.background, p: 2 }}>
                                                        {question.questionText ? (
                                                            <Typography variant="body2" color="text.secondary" mb={2}>
                                                                {question.questionText}
                                                            </Typography>
                                                        ) : null}                                                       
                                                        {renderTriggerSection(workflowQuestion)}
                                                    </Box>
                                                </Collapse>
                                            </Paper>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {droppableProvided.placeholder}
                        </Stack>
                    )}
                </StrictModeDroppable>
            </DragDropContext>

            {showAddQuestion && questionsAvailableToAdd.length > 0 && (
                <>
                    <Divider />
                    <FormControl fullWidth>
                        <InputLabel id="question-select-label">
                            Add question
                        </InputLabel>
                        <Select
                            labelId="question-select-label"
                            label="Add question"
                            value=""
                            onChange={(event) => {
                                const questionId = Number(event.target.value);
                                if (questionId) {
                                    onAddQuestion(questionId);
                                }
                            }}
                        >
                            {questionsAvailableToAdd.map((question) => (
                                <MenuItem key={question.id} value={question.id}>
                                    {question.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </>
            )}
        </Stack>
    );
};

export default QuestionsSection;
